/**
 * The one place that talks HTTP to the API. Every resource client goes through `request`, so cookies, JSON and
 * error decoding are handled the same way everywhere.
 */

/** Empty in development (Vite proxies /api and /v1); the API's origin in production builds. */
export const API_BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/+$/, "");

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const isApiError = (e: unknown, code?: string): e is ApiError => e instanceof ApiError && (code === undefined || e.code === code);

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export interface RequestOptions {
  signal?: AbortSignal;
}

/**
 * Turns an error body into an ApiError. Our API answers `{error: {code, message}}` (design doc §18.2);
 * Better Auth answers `{code, message}` at the top level.
 */
export function toApiError(status: number, body: unknown): ApiError {
  const b = (body ?? {}) as { error?: { code?: string; message?: string; details?: unknown }; code?: string; message?: string };
  const code = b.error?.code ?? b.code ?? `HTTP_${status}`;
  const message = b.error?.message ?? b.message ?? `The server answered ${status}`;
  return new ApiError(status, code, message, b.error?.details);
}

export async function request<T>(method: Method, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(API_BASE + path, {
      method,
      credentials: "include",
      headers: body === undefined ? {} : { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: opts.signal,
    });
  } catch (e) {
    if (opts.signal?.aborted) throw e;
    throw new ApiError(0, "NETWORK", "Can't reach the server. Check your connection and try again.");
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = null; }
  }
  if (!res.ok) throw toApiError(res.status, data);
  return data as T;
}

/** Encodes one path segment. */
export const seg = (s: string) => encodeURIComponent(s);
